/**
 * RTWO Event Bus — Event Publisher Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type { RTWOEventPriority, RTWOEventType } from '../domain-models';
import type { EventRegistryResult, IEventPublisherService } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { EBusInternalEventTypes } from '../events/ebus-internal-event-bus';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class EventPublisherService extends BaseEBusService implements IEventPublisherService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.EVENT_PUBLISHER;
  readonly serviceName = 'Event Publisher';
  readonly description =
    'Publishes events from RTWO modules to the event bus. No events published until real sources connect.';
  readonly integrations = ['Event Registry', 'Topic Registry', 'Routing Engine'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async publish(
    eventType: RTWOEventType,
    sourceModule: string,
    targetModule: string | null,
    _priority: RTWOEventPriority,
    payloadReference: string | null
  ): Promise<EventRegistryResult> {
    if (!this.engineSourceRegistry.isConfigured()) {
      return {
        success: false,
        eventId: null,
        error:
          'Event Publisher Not Active — Cannot publish events until real RTWO engine event sources are connected.',
        isPlaceholder: true,
        source: 'Event Bus — Waiting for Real Event Sources',
      };
    }

    const adapterResult = await this.engineSourceRegistry
      .getActiveAdapter()
      .publishEvent(eventType, sourceModule, targetModule, payloadReference);

    if (adapterResult?.event) {
      this.repositories.events.save(adapterResult.event);
      this.context.eventBus.emit(EBusInternalEventTypes.EVENT_PUBLISHED, {
        eventId: adapterResult.event.eventId,
        eventType,
        sourceModule,
        targetModule,
      });
    }

    return {
      success: false,
      eventId: null,
      error:
        'Event Publisher Not Active — Cannot publish events until real RTWO engine event sources are connected.',
      isPlaceholder: true,
      source: 'Event Bus — Waiting for Real Event Sources',
    };
  }

  getPublishedEventCount(): 0 {
    return 0;
  }
}
